"use client";

import { useState } from "react";
import { FetchError, ofetch } from "ofetch";
import useSWRMutation from "swr/mutation";
import * as z from "zod";

import type {
  RegisterUserApiErrorResponse,
  RegisterUserApiSuccessResponse,
} from "@/app/api/auth/register/route";
import Button from "@/components/atoms/Button";
import { Loader } from "@/components/atoms/Loader";
import TestingModeOnly from "@/components/blocks/TestingModeOnly";
import BirthDayInput from "@/components/forms/controls/BirthDayInput";
import Checkbox from "@/components/forms/controls/Checkbox";
import TextInput from "@/components/forms/controls/TextInput";
import Form from "@/components/forms/Form";
import { EmailCorrectnessConfirmationModal } from "@/components/organisms/EmailCorrectnessConfirmationModal";
import { formatToYYYYMMDD } from "@/helpers/date";
import { useZodForm } from "@/hooks/useZodForm";
import { useTranslation } from "@/i18n/client";
import { isValidPhoneNumber } from "@/lib/validators/isValidPhoneNumber";
import { AllowedLanguages, TempUserFromSocialCookie } from "@/types/generic";

interface RegisterFormProps {
  locale: AllowedLanguages;
  user?: TempUserFromSocialCookie | null;
}

export const RegisterForm = ({ locale, user }: RegisterFormProps) => {
  const { t } = useTranslation(locale);
  const [
    isEmailCorrectnessConfirmationModalOpen,
    setIsEmailCorrectnessConfirmationModalOpen,
  ] = useState(false);

  const nameMatch = user ? /^(.*?)\s(.*)$/.exec(user.name) : [];
  const firstName = nameMatch ? nameMatch[1] : "";
  const lastName = nameMatch ? nameMatch[2] : "";

  const schema = z.object({
    first_name: z.string().min(1, t("common.error.required")),
    last_name: z.string().min(1, t("common.error.required")),
    email: z.email(t("common.error.invalid_email")).trim().toLowerCase(),
    password: user
      ? z.string().trim().optional()
      : z.string().trim().min(8, t("common.error.password_length")),
    telephone: z
      .string()
      .min(1, t("common.error.required"))
      .refine((value) => {
        // keep only numbers
        const numberOnlyValue = value.replace(/[^0-9]/g, "");
        return isValidPhoneNumber(numberOnlyValue);
      }, t("common.error.phone_format"))
      .transform((val) => {
        // keep only numbers and transform to Connexys format
        let numberOnlyValue = val.replace(/[^0-9]/g, "");

        // transform show Belgium mobile number to full: 04 xxxxxxxx should be transformed to 324xxxxxxxx
        if (numberOnlyValue.startsWith("04") && numberOnlyValue.length === 10) {
          numberOnlyValue = numberOnlyValue.replace("04", "324");
        }

        // Connexys telephone number starts with 003 and has 13 digits (example: 0032496166716)
        return numberOnlyValue.padStart(13, "0");
      }),
    birthday: z
      .date({ error: t("common.error.birthday_format") })
      .transform((val) => {
        // Convert to 'YYYY-MM-DD'
        const formattedDate = formatToYYYYMMDD(val);
        // Convert 'YYYY-MM-DD' to '1904-MM-DD' because we don't ask for a real birt year, only for a day and month
        return `${1904}${formattedDate.slice(4)}`;
      }),
    postal_code: z.string().length(4, t("common.error.required")),
    privacy_statement: z.boolean().refine((val) => val, {
      message: t("common.error.privacy_statement_required"),
    }),
    linkedin_id: z.string().optional(),
    facebook_id: z.string().optional(),
    workid_id: z.string().optional(),
    user_confirmed_potentially_invalid_email: z.boolean().optional(),
  });

  const form = useZodForm({
    schema,
    defaultValues: {
      first_name: firstName,
      last_name: lastName,
      email: user ? user.email : "",
      user_confirmed_potentially_invalid_email: false,
      // Value should be true, but we set it as false so user has to change it
      privacy_statement: false,
    },
  });

  async function createUser(
    url: string,
    {
      arg,
    }: {
      arg: z.infer<typeof schema>;
    },
  ) {
    return await ofetch<RegisterUserApiSuccessResponse>(url, {
      method: "POST",
      body: {
        locale: locale,
        ...arg,
      },
    });
  }

  const { data, trigger, isMutating, error } = useSWRMutation<
    RegisterUserApiSuccessResponse,
    FetchError<RegisterUserApiErrorResponse>,
    string,
    z.infer<typeof schema>
  >("/api/auth/register", createUser, {
    onError: (error) => {
      if (error.data?.emailMayBeInvalid) {
        setIsEmailCorrectnessConfirmationModalOpen(true);
      }
    },
  });

  const isUserExists = error?.status === 409;
  const showForm = !isMutating && !data && !isUserExists;
  const handleSubmit = async (data: z.infer<typeof schema>) => {
    await trigger(data);
  };

  const handleConfirmEmailCorrectnessConfirmationModal = async () => {
    setIsEmailCorrectnessConfirmationModalOpen(false);
    form.setValue("user_confirmed_potentially_invalid_email", true);
    const submitForm = form.handleSubmit(handleSubmit);

    try {
      await submitForm();
    } catch {
      // do nothing
    }
  };

  const handleCancelEmailCorrectnessConfirmationModal = () => {
    setIsEmailCorrectnessConfirmationModalOpen(false);
    form.setValue("user_confirmed_potentially_invalid_email", true);
    // focus and scroll to the "email" field
    setTimeout(() => form.setFocus("email"), 500);
  };

  const inputClasses =
    "flex flex-col lg:grid lg:grid-cols-[250px_1fr] gap-2 lg:gap-8 items-stretch lg:items-center";
  const labelClasses = "!mb-0";

  return (
    <div className="px-6 py-6 lg:px-12 lg:py-10">
      {showForm && !isMutating && (
        <>
          <h5> {t("pages.myvivaldis_register.register_form.subtitle")}</h5>
          <div className="mb-6 text-sm italic text-black-400">
            {t("pages.myvivaldis_register.register_form.description")}
          </div>

          <Form
            form={form}
            className="flex flex-col gap-4"
            onSubmit={handleSubmit}
            noValidate={true}
          >
            <TextInput
              label={t("common.form.first_name.label")}
              placeholder={t("common.form.first_name.placeholder")}
              name="first_name"
              className={inputClasses}
              labelClasses={labelClasses}
              autoComplete="given-name"
              required
            />
            <TextInput
              label={t("common.form.last_name.label")}
              placeholder={t("common.form.last_name.placeholder")}
              name="last_name"
              className={inputClasses}
              labelClasses={labelClasses}
              autoComplete="family-name"
              required
            />
            <TextInput
              label={t("common.form.email.label")}
              placeholder={t("common.form.email.placeholder")}
              name="email"
              type="email"
              className={inputClasses}
              labelClasses={labelClasses}
              autoComplete="email"
              required
              disabled={!!user}
            />
            {!user && (
              <TextInput
                label={t("common.form.password.label")}
                placeholder={t("common.form.password.placeholder")}
                name="password"
                type="password"
                className={inputClasses}
                labelClasses={labelClasses}
                autoComplete="new-password"
                required
              />
            )}
            <TextInput
              label={t("common.form.phone.label")}
              placeholder={t("common.form.phone.placeholder")}
              name="telephone"
              type="tel"
              className={inputClasses}
              labelClasses={labelClasses}
              autoComplete="tel"
              required
            />
            <BirthDayInput
              label={t("common.form.birthday.label")}
              placeholder={t("common.form.birthday.placeholder")}
              className={inputClasses}
              labelClasses={labelClasses}
              name="birthday"
              locale={locale}
              initialValue={form.watch("birthday")}
              required
            />
            <TextInput
              label={t("common.form.postal_code.label")}
              placeholder={t("common.form.postal_code.placeholder")}
              name="postal_code"
              className={inputClasses}
              labelClasses={labelClasses}
              autoComplete="postal-code"
              required
            />
            {user && user?.provider === "linkedin" && (
              <TextInput
                label="linkedin_id"
                hiddenLabel
                name="linkedin_id"
                type="hidden"
                value={user.id}
              />
            )}
            {user && user?.provider === "facebook" && (
              <TextInput
                label="facebook_id"
                hiddenLabel
                name="facebook_id"
                type="hidden"
                value={user.id}
              />
            )}
            {user && user?.provider === "workid" && (
              <TextInput
                label="workid_id"
                hiddenLabel
                name="workid_id"
                type="hidden"
                value={user.id}
              />
            )}
            <Checkbox
              label={t(
                "pages.myvivaldis_register.register_form.privacy_statement.label",
              )}
              name="privacy_statement"
              className={"mt-6"}
              asBoolean
              checked={form.watch("privacy_statement")}
            />

            {error && !error?.data?.emailMayBeInvalid && (
              <div className="form-error text-lg">
                {t("pages.myvivaldis_register.register_form.error.message")}
              </div>
            )}

            <Button
              type="submit"
              className="btn-lg mx-auto mt-6 min-w-[200px]"
              disabled={isMutating}
            >
              {t("pages.myvivaldis_register.register_form.submit_button")}
            </Button>
          </Form>
        </>
      )}

      {isMutating && (
        <Loader size="medium" showMessages={true} locale={locale} />
      )}

      {!showForm && !isMutating && (
        <>
          {data?.ok ? (
            <div>
              <h2>
                {t("pages.myvivaldis_register.register_form.success.title")}
              </h2>
              <div className="text-lg text-black-400">
                {t("pages.myvivaldis_register.register_form.success.message")}
              </div>
              <TestingModeOnly>
                <div className="mt-4 rounded border-2 border-dashed bg-yellow-100 p-4">
                  <h6>Activation url (testing)</h6>
                  <a href={data.url ?? "#"} className="break-all text-sm">
                    {data.url}
                  </a>
                </div>
              </TestingModeOnly>
            </div>
          ) : (
            <div className="flex flex-col">
              <h2>
                {t(
                  "pages.myvivaldis_register.register_formuser_already_exists.title",
                )}
              </h2>
              <div>
                {t(
                  "pages.myvivaldis_register.register_formuser_already_exists.message",
                )}
              </div>
              <Button
                as="link"
                className="ml-auto mt-6"
                href={`/${locale}/myvivaldis`}
              >
                {t(
                  "pages.myvivaldis_register.register_formuser_already_exists.login_btn",
                )}
              </Button>
            </div>
          )}
        </>
      )}

      <EmailCorrectnessConfirmationModal
        email={form.watch("email")}
        locale={locale}
        open={isEmailCorrectnessConfirmationModalOpen}
        onCancel={handleCancelEmailCorrectnessConfirmationModal}
        onConfirm={handleConfirmEmailCorrectnessConfirmationModal}
      />
    </div>
  );
};
